import React, { useContext, useState, useEffect } from 'react'
import { DashboardContext } from '@/context/DashboardContext'
import { createPortal } from 'react-dom'
import NoProjects from '@/assets/no_projects_yet.png'
import { ProjectLayout } from '../dialog/projectLayout'
import { RenameProject } from '../dialog/renameProject'
import { Project } from './project'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Apple,
  Tablet,
  Smartphone,
  Globe,
  Laptop,
  Computer,
  MonitorSmartphone,
  X,
  Upload,
  Plus,
  ShieldQuestion,
} from 'lucide-react'
import { DeleteProductDialog } from '../products/deleteProductDialog'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export function Projects({ limit }) {
  const [openRenameProjectDialog, setOpenRenameProjectDialog] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false)
  const [productDialog, setProductDialog] = useState('')
  const [expandedProducts, setExpandedProducts] = useState(new Set())
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [currentProductId, setCurrentProductId] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [productName, setProductName] = useState('')
  const [platform, setPlatform] = useState('')
  const [status, setStatus] = useState('ideation')
  const [tagline, setTagline] = useState('')
  const [description, setDescription] = useState('')
  const [uniqueSellingProposition, setUniqueSellingProposition] = useState('')
  const [documents, setDocuments] = useState([])
  const [links, setLinks] = useState([])
  const { allProjects, filteredProjects, projectsLoading, unsortedProjects, products, setProducts, setAllProducts } =
    useContext(DashboardContext)
  const customFetch = useCustomFetch()

  // Initialize expandedProducts with all product IDs
  useEffect(() => {
    if (products.length > 0) {
      const allProductIds = new Set(products.map((product) => product.id))
      setExpandedProducts(allProductIds)
    }
  }, [products])

  const toggleProduct = (productId) => {
    const newExpandedProducts = new Set(expandedProducts)
    if (newExpandedProducts.has(productId)) {
      newExpandedProducts.delete(productId)
    } else {
      newExpandedProducts.add(productId)
    }
    setExpandedProducts(newExpandedProducts)
  }

  const getPlatformIcon = (platform, size = 16, className = 'text-gray-700') => {
    switch (platform) {
      case 'iOS':
        return <Apple size={size} className={className} strokeWidth={2} />
      case 'iPad OS':
        return <Tablet size={size} className={className} strokeWidth={2} />
      case 'Android':
        return <Smartphone size={size} className={className} strokeWidth={2} />
      case 'Web':
        return <Globe size={size} className={className} strokeWidth={2} />
      case 'Mac OS':
        return <Laptop size={size} className={className} strokeWidth={2} />
      case 'Windows':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Linux':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Multi-Platform':
        return <MonitorSmartphone size={size} className={className} strokeWidth={2} />
      default:
        return <Globe size={size} className={className} strokeWidth={2} />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'text-[#B2C920]'
      case 'Beta':
        return 'text-[#964EB2]'
      case 'Alpha':
        return 'text-[#CF3F75]'
      case 'Planning':
        return 'text-[#2C8FB1]'
      case 'Ideation':
        return 'text-[#DCAE1D]'
      default:
        return 'text-gray-500'
    }
  }

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'bg-[#B2C920]/20 text-[#B2C920] border border-[#B2C920]/30'
      case 'Beta':
        return 'bg-[#964EB2]/20 text-[#964EB2] border border-[#964EB2]/30'
      case 'Alpha':
        return 'bg-[#CF3F75]/20 text-[#CF3F75] border border-[#CF3F75]/30'
      case 'Planning':
        return 'bg-[#2C8FB1]/20 text-[#2C8FB1] border border-[#2C8FB1]/30'
      case 'Ideation':
        return 'bg-[#DCAE1D]/20 text-[#DCAE1D] border border-[#DCAE1D]/30'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleEditProduct = (product) => {
    setProductName(product.name)
    setTagline(product.tagline)
    setDescription(product.description)
    setUniqueSellingProposition(product.uniqueSellingProposition || '')
    setSelectedImage(product.logo)
    setDocuments(product.documents || [])
    setLinks(product.links || [])

    const platformValue = product.platform.toLowerCase().replace(' ', '').replace('-', '')
    setPlatform(platformValue)

    let statusValue = product.status.toLowerCase()
    if (statusValue === 'live') statusValue = 'active'
    setStatus(statusValue)

    setCurrentProductId(product.id)
    setEditDialogOpen(true)
  }

  const handleSaveProduct = async () => {
    if (!productName.trim() || !currentProductId) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!platform.trim()) return toast.error('Platform is required')
    if (!status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.id === currentProductId) {
          return {
            ...product,
            name: productName,
            tagline: tagline,
            description: description,
            uniqueSellingProposition,
            status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
            platform:
              platform === 'web'
                ? 'Web'
                : platform === 'ios'
                  ? 'iOS'
                  : platform === 'android'
                    ? 'Android'
                    : platform === 'macos'
                      ? 'Mac OS'
                      : platform === 'windows'
                        ? 'Windows'
                        : platform === 'linux'
                          ? 'Linux'
                          : platform === 'multiplatform'
                            ? 'Multi-Platform'
                            : 'Web',
            logo: selectedImage,
            links,
            documents,
          }
        }
        return product
      })
    )

    await customFetch('/updateProduct', 'PUT', {
      id: currentProductId,
      name: productName,
      tagline,
      description,
      uniqueSellingProposition,
      links,
      documents,
      status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform:
        platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      ...(selectedImage ? { logo: selectedImage } : {}),
    })

    resetForm()
    setEditDialogOpen(false)
  }

  const resetForm = () => {
    setProductName('')
    setPlatform('')
    setStatus('ideation')
    setTagline('')
    setDescription('')
    setUniqueSellingProposition('')
    setSelectedImage(null)
    setDocuments([])
    setLinks([])
    setCurrentProductId(null)
  }

  const handleDeleteProduct = async (productId) => {
    try {
      await customFetch(`/deleteProduct/${productId}`, 'DELETE')
      setAllProducts()
      toast.success('Product deleted successfully')
      setIsDeleteDialogOpen(false)
    } catch (err) {
      console.error('error deleting product', err)
      toast.error('Failed to delete product')
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      toast.error('File size should be less than 5MB')
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await customFetch('/uploadProductLogo', 'POST', formData, true)
      if (response?.url) {
        setSelectedImage(response.url)
        toast.success('Logo uploaded successfully')
      }
    } catch (error) {
      console.error('Error uploading logo:', error)
      toast.error('Failed to upload logo')
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="overflow-auto p-8 flex items-center justify-center">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-3xl font-bold">Projects</h1>
            <Button
              className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90"
              onClick={() => setShowCreateProjectDialog(true)}
            >
              <Plus size={16} />
              Add
            </Button>
          </div>
          <p className="text-gray-500 mb-8">Manage and monitor your projects</p>

          {projectsLoading && (
            <div className="flex justify-center items-center">
              <svg className="w-40 h-40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                <radialGradient id="a1" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
                  <stop offset="0" stopColor="#FF156D"></stop>
                  <stop offset=".3" stopColor="#FF156D" stopOpacity=".9"></stop>
                  <stop offset=".6" stopColor="#FF156D" stopOpacity=".6"></stop>
                  <stop offset=".8" stopColor="#FF156D" stopOpacity=".3"></stop>
                  <stop offset="1" stopColor="#FF156D" stopOpacity="0"></stop>
                </radialGradient>
                <circle
                  transform-origin="center"
                  fill="none"
                  stroke="url(#a1)"
                  strokeWidth="15"
                  strokeLinecap="round"
                  strokeDasharray="200 1000"
                  strokeDashoffset="0"
                  cx="100"
                  cy="100"
                  r="70"
                >
                  <animateTransform
                    type="rotate"
                    attributeName="transform"
                    calcMode="spline"
                    dur="2"
                    values="360;0"
                    keyTimes="0;1"
                    keySplines="0 0 1 1"
                    repeatCount="indefinite"
                  ></animateTransform>
                </circle>
                <circle
                  transform-origin="center"
                  fill="none"
                  opacity=".2"
                  stroke="#FF156D"
                  strokeWidth="15"
                  strokeLinecap="round"
                  cx="100"
                  cy="100"
                  r="70"
                ></circle>
              </svg>
            </div>
          )}
          {!projectsLoading && allProjects.length === 0 && (
            <div className="flex justify-center items-center my-11">
              <div className="flex items-center gap-[36px]">
                <div className="w-[441px]">
                  <p className="text-[#676d7f] dark:text-gray-400 text-xl font-bold font-['Manrope'] leading-[23px]">
                    No Projects Yet
                  </p>
                  <p className="w-[420px] mt-6 text-[#676d7f] dark:text-gray-400 text-base font-normal font-['Manrope'] leading-[23px]">
                    Add one now by clicking the Add button or by pasting a meeting link above.
                  </p>
                </div>
                <img className="w-[134px] h-[84px] mt-5 mx-auto" src={NoProjects} alt="No upcoming projects" />
              </div>
            </div>
          )}
          {!projectsLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Unassigned Projects Box - Only shown if there are unassigned projects */}
              {unsortedProjects && unsortedProjects.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-[#E4E4E4] dark:border-gray-700 p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 relative rounded-md overflow-hidden">
                      <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <ShieldQuestion size={32} className="text-gray-500" strokeWidth={2} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Unassigned Projects</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">Projects without a product</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-block px-3 py-1.5 text-sm font-medium rounded-full bg-gray-100/20 text-gray-600 border border-gray-300/30">
                          Unassigned
                        </span>
                      </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowCreateProjectDialog(true)
                          setProductDialog('')
                        }}
                      >
                        <Plus size={16} className="text-gray-600 dark:text-gray-400" />
                      </Button>
                    </div>
                  </div>

                  <div className="h-[320px] flex flex-col">
                    <div className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-[250px] text-gray-500 dark:text-gray-400">Project Name</TableHead>
                            <TableHead className="w-[150px] text-gray-500 dark:text-gray-400">Method</TableHead>
                            <TableHead className="w-[100px] text-center text-gray-500 dark:text-gray-400">
                              Sessions
                            </TableHead>
                            <TableHead className="w-[100px] text-gray-500 dark:text-gray-400">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                      </Table>
                    </div>
                    <div className="overflow-y-auto flex-1">
                      <Table>
                        <TableBody>
                          {unsortedProjects.map((project) => (
                            <Project
                              key={project.id}
                              project={project}
                              setOpenRenameDialog={setOpenRenameProjectDialog}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}

              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-[#E4E4E4] dark:border-gray-700 p-4"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 relative rounded-md overflow-hidden">
                      {product.logo ? (
                        <img
                          src={product.logo}
                          alt={`${product.name} Product Logo`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          {getPlatformIcon(product.platform, 32, getStatusColor(product.status))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{product.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">{product.tagline}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`inline-block px-3 py-1.5 text-sm font-medium rounded-full ${getStatusBadgeClasses(product.status)}`}
                        >
                          {product.status}
                        </span>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                          {getPlatformIcon(product.platform, 20)}
                          <span>{product.platform}</span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowCreateProjectDialog(true)
                          setProductDialog(product.id)
                        }}
                      >
                        <Plus size={16} className="text-gray-600 dark:text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditProduct(product)}>
                        <Pencil size={16} className="text-gray-600 dark:text-gray-400" />
                      </Button>
                    </div>
                  </div>

                  {product.projectsData && product.projectsData.length > 0 ? (
                    <div className="h-[320px] flex flex-col">
                      <div className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="w-[250px] text-gray-500 dark:text-gray-400">Project Name</TableHead>
                              <TableHead className="w-[150px] text-gray-500 dark:text-gray-400">Method</TableHead>
                              <TableHead className="w-[100px] text-center text-gray-500 dark:text-gray-400">
                                Sessions
                              </TableHead>
                              <TableHead className="w-[100px] text-gray-500 dark:text-gray-400">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                        </Table>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        <Table>
                          <TableBody>
                            {product.projectsData.map((project) => (
                              <Project
                                key={project.id}
                                project={project}
                                setOpenRenameDialog={setOpenRenameProjectDialog}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No projects yet. Click "Add Project" to create one.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreateProjectDialog &&
        createPortal(
          <ProjectLayout
            link=""
            importFile={false}
            handleClose={() => setShowCreateProjectDialog(false)}
            productId={productDialog}
          />,
          document.body
        )}

      {openRenameProjectDialog &&
        createPortal(
          <RenameProject
            projectId={openRenameProjectDialog.id}
            projectName={openRenameProjectDialog.name}
            projectDescription={openRenameProjectDialog.description}
            projectMethod={openRenameProjectDialog.method}
            projectProduct={openRenameProjectDialog.product}
            handleClose={() => setOpenRenameProjectDialog(null)}
          />,
          document.body
        )}

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-[900px] rounded-2xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-xl font-semibold">Edit Product</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid gap-6">
              {/* Logo & Product details */}
              <div className="grid grid-cols-[120px_1fr] gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Logo</label>
                  <div className="h-[120px] w-[120px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative">
                    {selectedImage ? (
                      <div className="relative w-full h-full">
                        <img
                          src={selectedImage}
                          alt="Selected Logo"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => setSelectedImage(null)}
                          className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md"
                        >
                          <X size={16} className="text-gray-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center p-2">
                        <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                        <p className="text-xs text-gray-600">
                          <label className="text-[#FF4A8E] hover:underline cursor-pointer">
                            Add logo
                            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                          </label>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="edit-product-name" className="text-sm font-medium">
                      Name*
                    </label>
                    <input
                      id="edit-product-name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="edit-tagline" className="text-sm font-medium">
                      Tagline*
                    </label>
                    <input
                      id="edit-tagline"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter a short, catchy tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="edit-platform" className="text-sm font-medium">
                    Platform*
                  </label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger id="edit-platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-700" />
                          <span>Web</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ios">
                        <div className="flex items-center gap-2">
                          <Apple size={16} className="text-gray-700" />
                          <span>iOS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="android">
                        <div className="flex items-center gap-2">
                          <Smartphone size={16} className="text-gray-700" />
                          <span>Android</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="macos">
                        <div className="flex items-center gap-2">
                          <Laptop size={16} className="text-gray-700" />
                          <span>Mac OS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="windows">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Windows</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="linux">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Linux</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="multiplatform">
                        <div className="flex items-center gap-2">
                          <MonitorSmartphone size={16} className="text-gray-700" />
                          <span>Multi-Platform</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="edit-status" className="text-sm font-medium">
                    Status*
                  </label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="edit-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideation">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#DCAE1D]"></span>
                          <span>Ideation</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="planning">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#2C8FB1]"></span>
                          <span>Planning</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="alpha">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#CF3F75]"></span>
                          <span>Alpha</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="beta">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#964EB2]"></span>
                          <span>Beta</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#B2C920]"></span>
                          <span>Active</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-description" className="text-sm font-medium">
                  Description*
                </label>
                <textarea
                  id="edit-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Describe your product in detail"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-usp" className="text-sm font-medium">
                  Unique Selling Proposition*
                </label>
                <textarea
                  id="edit-usp"
                  value={uniqueSellingProposition}
                  onChange={(e) => setUniqueSellingProposition(e.target.value)}
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="What makes your product special? What's your competitive advantage?"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90" onClick={handleSaveProduct}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isDeleteDialogOpen && productToDelete && (
        <DeleteProductDialog
          productId={productToDelete.id}
          productName={productToDelete.name}
          handleClose={() => setIsDeleteDialogOpen(false)}
          handleDelete={() => handleDeleteProduct(productToDelete.id)}
        />
      )}
    </div>
  )
}
